import {
  type Flux,
  GENRES_NOEUD,
  type GenreNoeud,
  LIBELLES_GENRE_NOEUD,
  type UniteFlux,
} from '@admin-pmvb/core'
import { Link } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  cheminDe,
  cheminLien,
  disposerFlux,
  HAUTEUR_LIGNE,
  type NoeudPlace,
} from './flux-disposition'
import { Icone, type NomIcone } from './Icone'

// Diagramme de flux : d'où ça vient, quelle règle s'applique, où ça aboutit. Survoler un nœud ou
// une bande allume son chemin et ouvre une bulle (qui fait l'étape, combien, avec quels
// automatismes) ; un clic l'épingle, pour le téléphone et pour suivre un lien.

/** État d'un automatisme, pour les pastilles de la bulle. */
export type EtatJob = { libelle: string; cadence: string; enEchec: boolean }

const UNITES: Record<UniteFlux, [string, string]> = {
  mail: ['mail', 'mails'],
  piece: ['pièce', 'pièces'],
  operation: ['opération', 'opérations'],
}

const ICONES_GENRE: Record<GenreNoeud, NomIcone> = {
  entree: 'arrow_forward',
  regle: 'rule',
  claude: 'auto_awesome',
  humain: 'front_hand',
  ecarte: 'close',
  abouti: 'check_circle',
  echec: 'warning',
}

function compte(n: number, unite: UniteFlux) {
  return `${n} ${UNITES[unite][n > 1 ? 1 : 0]}`
}

/** « 12 mails » ; « 6 mails · 9 pièces » pour les pièces jointes (l'unité change). */
function volume(n: NoeudPlace): string {
  const parUnite = (liens: readonly { valeur: number; unite: UniteFlux }[]) => {
    const r = new Map<UniteFlux, number>()
    for (const l of liens) r.set(l.unite, (r.get(l.unite) ?? 0) + l.valeur)
    return r
  }
  const entree = parUnite(n.entrant)
  const sortie = parUnite(n.sortant)
  const unites = new Map<UniteFlux, number>()
  for (const [u, v] of [...entree, ...sortie]) unites.set(u, Math.max(unites.get(u) ?? 0, v))
  return [...unites].map(([u, v]) => compte(v, u)).join(' · ')
}

type Cible = { noeud: string } | { lien: string }

export function DiagrammeFlux({
  flux,
  jobs,
  hauteurMax = 620,
  largeurMin = 1060,
  largeurMax = Number.POSITIVE_INFINITY,
  largeurLibelleFin = 180,
  titre,
}: {
  flux: Flux
  jobs: ReadonlyMap<string, EtatJob>
  hauteurMax?: number
  largeurMin?: number
  /** Un diagramme de deux colonnes ne s'étire pas sur tout l'écran. */
  largeurMax?: number
  largeurLibelleFin?: number
  titre: string
}) {
  const conteneur = useRef<HTMLDivElement>(null)
  const [largeur, setLargeur] = useState(largeurMin)
  const [survol, setSurvol] = useState<Cible | null>(null)
  const [epingle, setEpingle] = useState<Cible | null>(null)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [tableau, setTableau] = useState(false)

  useEffect(() => {
    const el = conteneur.current
    if (!el) return
    const mesurer = () =>
      setLargeur(Math.min(largeurMax, Math.max(largeurMin, Math.floor(el.clientWidth))))
    mesurer()
    const obs = new ResizeObserver(mesurer)
    obs.observe(el)
    return () => obs.disconnect()
  }, [largeurMin, largeurMax])

  const d = useMemo(
    () => disposerFlux(flux, { largeur, hauteurMax, largeurLibelleFin }),
    [flux, largeur, hauteurMax, largeurLibelleFin],
  )
  const noeuds = useMemo(() => new Map(d.noeuds.map((n) => [n.id, n])), [d])
  const actif = epingle ?? survol
  const chemin = useMemo(() => {
    if (!actif) return null
    if ('noeud' in actif) return cheminDe({ noeud: actif.noeud }, d.liens)
    const l = d.liens.find((x) => x.id === actif.lien)
    return l ? cheminDe({ lien: l }, d.liens) : null
  }, [actif, d])

  const suivre = (e: React.MouseEvent) => {
    const r = conteneur.current?.getBoundingClientRect()
    if (!r) return
    setPosition({
      x: e.clientX - r.left + (conteneur.current?.scrollLeft ?? 0),
      y: e.clientY - r.top,
    })
  }
  const choisir = (c: Cible, e: React.MouseEvent) => {
    e.stopPropagation()
    suivre(e)
    setEpingle((avant) => (JSON.stringify(avant) === JSON.stringify(c) ? null : c))
  }

  if (d.noeuds.length === 0) return <p className="discret">Rien n’est arrivé sur la période.</p>

  const bulle = actif && <Bulle cible={actif} d={d} noeuds={noeuds} jobs={jobs} />
  return (
    <div className="flux">
      <div
        className="flux-defilement"
        ref={conteneur}
        onClick={() => setEpingle(null)}
        onMouseLeave={() => setSurvol(null)}
      >
        <svg
          width={d.largeur}
          height={d.hauteur}
          viewBox={`0 0 ${d.largeur} ${d.hauteur}`}
          role="img"
          aria-label={`${titre} : ${d.noeuds.length} étapes, ${d.liens.length} passages. Détail dans le tableau.`}
        >
          {flux.colonnes.map((c, i) => (
            <text key={c} x={d.colonnesX[i]} y={12} className="flux-colonne">
              {c.toUpperCase()}
            </text>
          ))}
          {d.groupes.map((g) => (
            <text key={g.libelle} x={g.x} y={g.y} className="flux-groupe">
              {g.libelle}
            </text>
          ))}
          <g>
            {d.liens.map((l) => {
              const genre = noeuds.get(l.vers)!.genre
              const etat = chemin ? (chemin.liens.has(l.id) ? 'allume' : 'eteint') : 'repos'
              return (
                <g key={l.id}>
                  <path
                    d={cheminLien(l)}
                    className={`flux-lien genre-${genre} ${etat}`}
                    strokeWidth={l.epaisseur}
                  />
                  <path
                    d={cheminLien(l)}
                    className="flux-cible"
                    strokeWidth={Math.max(l.epaisseur, 10)}
                    onMouseEnter={(e) => {
                      suivre(e)
                      setSurvol({ lien: l.id })
                    }}
                    onMouseMove={suivre}
                    onClick={(e) => choisir({ lien: l.id }, e)}
                  />
                </g>
              )
            })}
          </g>
          {d.noeuds.map((n) => {
            const eteint = chemin && !chemin.noeuds.has(n.id)
            return (
              <g
                key={n.id}
                data-noeud={n.id}
                className={eteint ? 'flux-noeud eteint' : 'flux-noeud'}
                onMouseEnter={(e) => {
                  suivre(e)
                  setSurvol({ noeud: n.id })
                }}
                onMouseMove={suivre}
                onClick={(e) => choisir({ noeud: n.id }, e)}
              >
                <rect
                  x={n.x}
                  y={n.yBarre}
                  width={10}
                  height={n.hBarre}
                  rx={2}
                  className={`flux-barre genre-${n.genre}`}
                />
                <rect
                  x={n.x - 4}
                  y={n.y}
                  width={d.largeursLibelle[n.colonne]! + 20}
                  height={n.hEmplacement}
                  className="flux-cible-noeud"
                />
                <text
                  x={n.x + 15}
                  y={n.y + n.hEmplacement / 2 - ((n.lignes.length - 1) * HAUTEUR_LIGNE) / 2 + 4}
                  className="flux-libelle"
                >
                  {n.lignes.map((ligne, i) => (
                    <tspan key={ligne} x={n.x + 15} dy={i === 0 ? 0 : HAUTEUR_LIGNE}>
                      {ligne}
                      {i === n.lignes.length - 1 && (
                        <tspan className="flux-valeur"> {n.chiffre}</tspan>
                      )}
                    </tspan>
                  ))}
                </text>
              </g>
            )
          })}
        </svg>
        {bulle && (
          <div
            className={epingle ? 'flux-bulle epinglee' : 'flux-bulle'}
            style={{
              left: Math.min(position.x + 14, d.largeur - 320),
              top: Math.min(position.y + 14, d.hauteur - 40),
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {bulle}
          </div>
        )}
      </div>
      <p className="flux-glisser discret">Faites glisser le diagramme pour voir la suite.</p>
      <div className="flux-pied">
        <ul className="flux-legende" aria-label="Légende : qui fait l’étape">
          {GENRES_NOEUD.filter((g) => d.noeuds.some((n) => n.genre === g)).map((g) => (
            <li key={g}>
              <span className={`flux-pastille genre-${g}`} aria-hidden />
              {LIBELLES_GENRE_NOEUD[g]}
            </li>
          ))}
        </ul>
        <button type="button" className="bouton-discret" onClick={() => setTableau(!tableau)}>
          {tableau ? 'Masquer le tableau' : 'Voir en tableau'}
        </button>
      </div>
      {tableau && (
        <div className="flux-tableau">
          <table>
            <thead>
              <tr>
                <th scope="col">De</th>
                <th scope="col">Vers</th>
                <th scope="col">Volume</th>
              </tr>
            </thead>
            <tbody>
              {d.liens.map((l) => (
                <tr key={l.id}>
                  <td>{noeuds.get(l.de)!.libelle}</td>
                  <td>{noeuds.get(l.vers)!.libelle}</td>
                  <td className="mono">{compte(l.valeur, l.unite)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function Bulle({
  cible,
  d,
  noeuds,
  jobs,
}: {
  cible: Cible
  d: ReturnType<typeof disposerFlux>
  noeuds: ReadonlyMap<string, NoeudPlace>
  jobs: ReadonlyMap<string, EtatJob>
}) {
  if ('lien' in cible) {
    const l = d.liens.find((x) => x.id === cible.lien)
    if (!l) return null
    return (
      <>
        <strong>
          {noeuds.get(l.de)!.libelle} → {noeuds.get(l.vers)!.libelle}
        </strong>
        <span className="mono">{compte(l.valeur, l.unite)}</span>
      </>
    )
  }
  const n = noeuds.get(cible.noeud)
  if (!n) return null
  return (
    <>
      <strong>{n.libelle}</strong>
      <span className="mono">{volume(n)}</span>
      <span className={`flux-genre genre-${n.genre}`}>
        <Icone nom={ICONES_GENRE[n.genre]} />
        {n.genre === 'humain' ? 'Un humain' : LIBELLES_GENRE_NOEUD[n.genre]}
      </span>
      <p>{n.description}</p>
      {n.details && (
        <ul>
          {n.details.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      )}
      {n.jobs && n.jobs.length > 0 && (
        <div className="flux-jobs">
          {n.jobs.map((j) => {
            const e = jobs.get(j)
            return (
              <span
                key={j}
                className={e ? (e.enEchec ? 'flux-job echec' : 'flux-job') : 'flux-job absent'}
              >
                <span className="flux-job-point" aria-hidden />
                {e ? `${e.libelle} · ${e.cadence}` : `${j} : non planifié sur ce serveur`}
              </span>
            )
          })}
        </div>
      )}
      {n.lien && (
        <Link to={n.lien} className="lien-fleche">
          Ouvrir
          <Icone nom="arrow_forward" />
        </Link>
      )}
    </>
  )
}
